/**
 * RWBR — THE ART LIBRARY TAXONOMY.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  TWO LAYERS, AND THE REASON WHY.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The engine emits **95 distinct `momentTag`s** across a season (measured, not
 * estimated — see `scripts/art-coverage.ts`). Forty of them are Race Cam
 * combinations: `cam.middle.smart`, `cam.top.heat`, `cam.duel.reflex`, and so on.
 *
 * **You cannot tag a photograph `cam.middle.smart`.** A photograph does not know
 * which quality the track card called. It knows it is a car passing another car
 * on the outside. The engine's tag is a machine fact about *why* the pass
 * happened; the photo is a human fact about *what it looks like*.
 *
 * So there are two layers and they never mix:
 *
 *   LAYER 1 — `momentTag` (95, machine, already exists on every RuleEvent)
 *   LAYER 2 — `SceneTag`  (this file, ~20, human, what the picture depicts)
 *
 * A human tags LAYER 2. `MOMENT_TO_SCENE` below resolves LAYER 1 onto it. The
 * person filling the library never sees a momentTag, and the engine never needs
 * to know a photograph exists.
 *
 * ── WHY THIS MATTERS MORE THAN IT SOUNDS ───────────────────────────────────
 *
 * If the two layers were one, every new rule that emits a new momentTag would
 * silently orphan itself — the moment would fire and no picture would ever
 * match, with nothing to say so. `unmappedMomentTags()` exists to make that
 * impossible: it is asserted in the tests against the tags a real season
 * produces, so a new rule that forgets its scene fails the build.
 */

/**
 * The four categories printed on the track cards. Declared here rather than
 * imported because the engine carries `category` as a plain string — this is
 * the art layer's own closed reading of it, verified against
 * `src/data/track-cards.json`.
 */
export const TRACK_CATEGORIES = [
  'Super Speedway',
  'Intermediate Track',
  'Short Track',
  'Road Course',
] as const;
export type TrackCategory = (typeof TRACK_CATEGORIES)[number];

// ---------------------------------------------------------------------------
// Layer 2 — what a photograph actually shows
// ---------------------------------------------------------------------------

/**
 * The tags a human applies to a photo. Deliberately small: every one of these
 * is answerable by looking at the picture for one second, with no knowledge of
 * the rules.
 */
export const SCENE_TAGS = [
  'pack-racing',        // cars three-wide, field strung out, no single subject
  'pass',               // one car clearly taking another
  'side-by-side',       // two cars fighting, neither ahead
  'leader-alone',       // a car out front with clear air
  'pit-stop',           // crew over the wall, car stationary
  'pit-trouble',        // something going wrong in the pit box
  'pit-lane',           // pit road, cars entering or leaving, no stop in progress
  'wreck',              // contact, debris, smoke, the moment of impact
  'big-wreck',          // multi-car, the whole field collected
  'spin',               // a car sideways or backwards, no major damage
  'mechanical',         // smoke, fluid, a car slowing or stopped, engine trouble
  'caution',            // pace car, yellow flag, field bunched behind it
  'cockpit',            // inside the car — helmet, hands, window net
  'crew',               // crew at work away from a stop; pit box, timing stand
  'paddock',            // garage, transporter, between-sessions, quiet
  'driver-portrait',    // a driver out of the car, helmet off or on
  'confrontation',      // drivers or crews facing off, feud energy
  'victory-lane',       // the win — celebration, trophy, confetti
  'crowd',              // grandstands, spectators, the scale of the place
  'track-atmosphere',   // the circuit itself, empty track, sky, scale
  'start-finish',       // the flagstand, green or checkered, the line
  'restart',            // double-file, field bunched, about to go green
] as const;

export type SceneTag = (typeof SCENE_TAGS)[number];

/**
 * ART_AND_FEEL.md's camera vocabulary, kept as a separate axis from the scene.
 * A pit stop can be shot from the stands or from a foot away; both are
 * `pit-stop`, and the selector should be able to prefer one.
 */
export const SHOT_TYPES = [
  'cockpit',      // in the car
  'trackside',    // low and close, wheel or wall level
  'stands',       // eye level from the grandstand, cars flashing past
  'aerial',       // from the blimp — geometry, scale
  'pit-box',      // in among the crew
  'paddock',      // garage and infield
  'portrait',     // a person, framed as a person
] as const;
export type ShotType = (typeof SHOT_TYPES)[number];

/**
 * How loud the picture is. NARRATION_MODEL §5 asks for selection by
 * `momentTag × trackCategory × intensity`; this is that third axis.
 */
export const INTENSITIES = ['calm', 'normal', 'hot'] as const;
export type Intensity = (typeof INTENSITIES)[number];

/** Conditions, because a wet photo under a dry race breaks the fiction hard. */
export const CONDITIONS = ['dry', 'wet', 'night', 'dusk'] as const;
export type Condition = (typeof CONDITIONS)[number];

// ---------------------------------------------------------------------------
// Rarity
// ---------------------------------------------------------------------------

/**
 * How special a photograph is.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  RARITY IS NOT "SHOWS LESS OFTEN". IT IS "SAVED FOR A BIGGER MOMENT".
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The obvious reading of rarity — roll a dice, show the legendary one 2% of the
 * time — produces a photograph you paid attention to and then never see, firing
 * at random on a routine mid-pack pass. That is worse than not having tiers at
 * all: the best picture in the library gets spent on nothing.
 *
 * So each tier carries a **significance floor**. A legendary photo is not
 * eligible until the moment itself is big — a wreck, a lead change on the last
 * lap, the win. When it finally appears it lands on something worth landing on,
 * which is what makes it feel legendary rather than merely infrequent.
 *
 * The frequency weight is the *second* lever, not the first. Among photos that
 * clear the floor, commons come up more often. And the caps stop a rare from
 * being burned twice in one afternoon.
 */
export const RARITIES = ['common', 'uncommon', 'rare', 'super-rare', 'legendary'] as const;
export type Rarity = (typeof RARITIES)[number];

export interface RarityRule {
  /**
   * The moment must be at least this significant, on the 0-100 scale in
   * `momentSignificance()`. A common photo has no floor and fits anywhere.
   */
  minSignificance: number;
  /** Relative pick weight among photos that clear the floor. */
  weight: number;
  /** Most appearances in one race. */
  maxPerRace: number;
  /** Most appearances in one season. `Infinity` for the workhorses. */
  maxPerSeason: number;
  /** Shown in the tagger so the choice is legible while tagging. */
  blurb: string;
}

/**
 * The tiers.
 *
 * The floors are set against the weights in `momentSignificance()`:
 *   25  a routine race-cam call        30  a quality challenge
 *   50  a duel                          55  a mechanical problem
 *   65  a lead change                   70  a pit violation
 *   85  a caution                      100  a wreck
 *
 * So `rare` at 50 means "a duel or better", and `legendary` at 85 means "a
 * caution or a wreck" — roughly three or four moments in a whole race.
 */
export const RARITY: Readonly<Record<Rarity, RarityRule>> = Object.freeze({
  common: {
    minSignificance: 0,
    weight: 1.0,
    maxPerRace: Infinity,
    maxPerSeason: Infinity,
    blurb: 'The workhorse. Fits any moment, shows most often.',
  },
  uncommon: {
    minSignificance: 30,
    weight: 0.55,
    maxPerRace: 5,
    maxPerSeason: Infinity,
    blurb: 'Needs a real racing moment — a challenge or better.',
  },
  rare: {
    minSignificance: 50,
    weight: 0.3,
    maxPerRace: 3,
    maxPerSeason: Infinity,
    blurb: 'Held back for a duel, a problem, or something louder.',
  },
  'super-rare': {
    minSignificance: 70,
    weight: 0.15,
    maxPerRace: 1,
    maxPerSeason: 6,
    blurb: 'Only for the big ones — a pit violation, a caution, a wreck.',
  },
  legendary: {
    minSignificance: 85,
    weight: 0.08,
    maxPerRace: 1,
    maxPerSeason: 2,
    blurb: 'The shot of the season. A caution or a wreck, and at most twice a year.',
  },
});

/**
 * One row of the library. `trackCategories` and `conditions` empty mean "fits
 * anywhere" — the common case, and deliberately the default, so a person
 * tagging a hundred photos only has to narrow the ones that genuinely need it.
 */
export interface ArtImage {
  /** Defaults to `common` when absent, so an untiered library behaves as before. */
  rarity?: Rarity;
  id: string;
  /**
   * What a person calls this photo. SEPARATE from the uploaded filename on
   * purpose: `originalName` is provenance — where the file came from — and
   * renaming a picture must not destroy that. Absent means nobody has named it
   * yet, and the UI falls back to `suggestTitle(originalName)`.
   */
  title?: string;
  /** Storage path or public URL. */
  url: string;
  scenes: SceneTag[];
  shot: ShotType | null;
  intensity: Intensity | null;
  /** Empty = suits any track. */
  trackCategories: TrackCategory[];
  /** Empty = suits any conditions. */
  conditions: Condition[];
  credit?: string;
  notes?: string;
}

// ---------------------------------------------------------------------------
// Layer 1 → Layer 2
// ---------------------------------------------------------------------------

/**
 * Every `momentTag` the engine emits, resolved to the scenes that could
 * illustrate it, best first.
 *
 * Order matters: the selector prefers earlier entries, so put the scene that
 * *is* the moment first and the acceptable fallbacks after. `trouble.caution`
 * leads with `caution` and falls back to `wreck`, because a caution photo is
 * the moment and a wreck photo is the cause.
 *
 * The forty `cam.*` tags collapse hard on purpose. `cam.top.heat` and
 * `cam.top.skill` are the same picture — a battle in the top group — because
 * the difference between them is a die roll, not a visual.
 */
export const MOMENT_TO_SCENE: Readonly<Record<string, readonly SceneTag[]>> = Object.freeze({
  // ── Race Cam: the quality is invisible; only the GROUP reads on camera ────
  'cam.top': ['pass', 'side-by-side', 'leader-alone'],
  'cam.middle': ['pack-racing', 'pass', 'side-by-side'],
  'cam.bottom': ['pack-racing', 'pass'],
  'cam.duel': ['side-by-side', 'pass', 'confrontation'],

  // ── Challenges and duels ─────────────────────────────────────────────────
  'challenge.sequence': ['pass', 'side-by-side', 'pack-racing'],
  'challenge.no-defender': ['leader-alone', 'pass'],
  'challenge.void-triangle': ['mechanical', 'spin'],
  'challenge.void-triples': ['pack-racing'],
  'duel.called': ['side-by-side', 'confrontation'],
  'duel.win': ['pass', 'side-by-side'],
  'duel.loss': ['side-by-side', 'spin'],
  'duel.chip-win': ['pass', 'side-by-side'],
  'duel.declined': ['pack-racing'],
  'duel.void-triangle': ['mechanical'],

  // ── Trouble and wrecks ───────────────────────────────────────────────────
  'trouble.caution': ['caution', 'wreck'],
  'trouble.multi-car': ['wreck', 'big-wreck'],
  'trouble.collected': ['wreck', 'big-wreck'],
  'trouble.no-contact': ['spin', 'pack-racing'],
  'trouble.escaped': ['spin', 'pack-racing'],
  'trouble.chip-saved': ['spin', 'pass'],
  'wreck.big-one': ['big-wreck', 'wreck'],
  'wreck.big-trouble': ['big-wreck', 'wreck'],

  // ── Problems — mechanical, with a couple of visual specialisations ────────
  'problem.engine': ['mechanical'],
  'problem.tire': ['mechanical', 'pit-lane'],
  'problem.brake': ['mechanical', 'spin'],
  'problem.handling': ['mechanical', 'spin'],
  'problem.cooling': ['mechanical'],
  'problem.electrical': ['mechanical'],
  'problem.drivetrain': ['mechanical'],
  'problem.fluids': ['mechanical'],
  'problem.contact': ['wreck', 'spin'],
  'problem.driver': ['cockpit', 'driver-portrait'],
  'problem.pit': ['pit-trouble', 'pit-stop'],
  'problem.unspecified': ['mechanical'],
  'problem.averted': ['cockpit', 'pack-racing'],
  'problem.no-driver': ['track-atmosphere'],

  // ── The pits ─────────────────────────────────────────────────────────────
  'pit.fast-pit': ['pit-stop', 'crew'],
  'pit.box-swap': ['pit-lane', 'pit-stop'],
  'pit.no-swap': ['pit-lane'],
  'pit.violation': ['pit-trouble', 'pit-lane'],
  'pit.chip-reorder': ['pit-lane', 'pit-stop'],
  'pit.lead-change': ['pass', 'pit-lane'],
  'pit.lead-held': ['leader-alone', 'pit-lane'],
  'pit.lead-held-chip': ['leader-alone', 'pit-stop'],
  'pit.no-challenger': ['leader-alone'],

  // ── Flags, gaps, restarts ────────────────────────────────────────────────
  'turn.yellow': ['caution', 'restart'],
  // The laps-down sub-group's two ways back onto the lead lap. Both happen
  // under caution with the field circulating, so the picture is the pace car
  // and the queue behind it — a car being waved by is not a racing shot.
  'yellow.lucky-dog': ['caution', 'restart', 'pit-lane'],
  'yellow.wave-around': ['caution', 'restart'],
  'yellow.wave-around.none': ['caution'],
  'gap.filled-green': ['pack-racing', 'pass'],
  'gap.filled-yellow': ['caution', 'restart'],
  'gap.none': ['pack-racing'],
  'turn.final-black-five': ['start-finish', 'pack-racing'],
  'turn.final-bottom-disregarded': ['pack-racing'],

  // ── The restart prelude (EZRA RSE) ───────────────────────────────────────
  // Every one of these happens with the field bunched double-file and about to
  // go green, so `restart` leads all of them — that IS the picture. The second
  // scene is what the specific result looks like once they are racing.
  'restart.shaky': ['restart', 'pack-racing'],
  'restart.violation': ['restart', 'pit-lane'],
  'restart.great': ['restart', 'pass'],
  'restart.problem': ['restart', 'mechanical'],
  'restart.trouble': ['restart', 'wreck'],
  'restart.driver-error': ['restart', 'spin'],
  'restart.roughed-up': ['restart', 'pack-racing'],
  'restart.rs-trait': ['restart', 'cockpit'],
  // The RS trait's extra pre-turn duel (D-EZ-23). A duel is a duel — the same
  // scenes the ordinary one uses — but it happens on a restart, so `restart`
  // rides along as a fallback.
  'restart-duel.win': ['side-by-side', 'pass', 'restart'],
  'restart-duel.chip-win': ['side-by-side', 'pass', 'restart'],
  'restart-duel.loss': ['side-by-side', 'confrontation', 'restart'],
  'restart-duel.declined': ['restart', 'pack-racing'],
  'restart-duel.void-triangle': ['restart', 'mechanical'],
  'restart.turn-cancelled': ['caution', 'restart'],

  // ── The Bottom Group Churn (EZRA-S2 §3.12) ───────────────────────────────
  // Twenty-odd cars at the back, several of them a lap down, racing each other
  // for something nobody in the grandstand is watching. That is `pack-racing`
  // deep in the field — so no `leader-alone`, no `start-finish`, and never
  // `victory-lane`. The Porch is the one moment with any glamour to it: a
  // bottom-group car putting a wheel on the middle group's last man.
  'churn.challenges': ['pack-racing', 'side-by-side'],
  'churn.porch': ['side-by-side', 'pass', 'pack-racing'],
  'churn.duel': ['side-by-side', 'confrontation'],
  'churn-duel.win': ['side-by-side', 'pass', 'pack-racing'],
  'churn-duel.chip-win': ['side-by-side', 'pass', 'pack-racing'],
  'churn-duel.loss': ['side-by-side', 'spin', 'confrontation'],
  'churn-duel.declined': ['pack-racing'],
  'churn-duel.void-triangle': ['mechanical', 'pack-racing'],
  // A Churn that started and did nothing, and a Churn the one-third limit waved
  // off. Both are non-events on the racing surface, so they get the picture of
  // the race going on around them rather than a picture of themselves.
  'churn.aborted': ['pack-racing', 'track-atmosphere'],
  'churn.ignored': ['pack-racing', 'track-atmosphere'],
  // A caution the Churn raised that has not taken effect yet — the flagstand is
  // still green and there is already a car in the fence.
  'churn.yellow-deferred': ['wreck', 'caution'],

  // ── The photo finish (EZRA-TP) ───────────────────────────────────────────
  // These are the only moments in the game that happen AT the line, so
  // `start-finish` leads all four — the flagstand is literally the subject.
  // A completed pass gets `pass` first because that is what the picture shows;
  // a defended one is `side-by-side`, because nobody got by.
  'pfa.round': ['start-finish', 'pack-racing'],
  'pfa.won-the-race': ['start-finish', 'victory-lane', 'pass'],
  'pfa.passed': ['pass', 'start-finish', 'side-by-side'],
  'pfa.defended': ['side-by-side', 'start-finish'],
  'pfa.attack-failed': ['side-by-side', 'pack-racing'],

  // ── Drafting ─────────────────────────────────────────────────────────────
  'draft.event': ['pack-racing', 'side-by-side'],
  'draft.event.double': ['pack-racing'],
  'draft.missed': ['pack-racing'],

  // ── Feuds and tempers ────────────────────────────────────────────────────
  'feud.none': ['confrontation', 'pack-racing'],
  'feud.wreck': ['wreck', 'confrontation'],
  'feud.demote': ['confrontation'],
  'fume.wreck': ['wreck', 'confrontation'],
  'fume.demote': ['confrontation'],

  // ── RARE ─────────────────────────────────────────────────────────────────
  'rare.surge': ['pass', 'pack-racing'],
  'rare.black-flag': ['pit-lane', 'confrontation'],
  'rare.driver-error': ['spin', 'cockpit'],
  'rare.driver-incident': ['cockpit', 'driver-portrait'],
  'rare.driver-flagged-for-aggressive-driving': ['confrontation', 'pit-lane'],
  'rare.pit-error': ['pit-trouble', 'crew'],
  'rare.pit-crew-injury': ['pit-trouble', 'crew'],
  'rare.pit-crew-incident': ['pit-trouble', 'crew'],
  'rare.rain-threat': ['track-atmosphere', 'crowd'],
  'rare.all-drivers-lose-quality': ['track-atmosphere'],
  'rare.suspended': ['confrontation', 'pit-lane'],
  // D-EZ-88 — the RARE side effects, applied.
  'rare.rain-threat.set': ['track-atmosphere', 'crowd'],
  'rare.chips-lost': ['track-atmosphere', 'caution'],
  'rare.red-flag': ['caution', 'track-atmosphere'],
  'rare.pit-grade-lowered': ['pit-trouble', 'crew'],
  'rare.quality-lost': ['track-atmosphere'],
  'rain.holds-off': ['track-atmosphere', 'crowd'],
  'rain.delay': ['caution', 'track-atmosphere'],
  'race.rain-shortened': ['track-atmosphere', 'start-finish'],
  'race.cancelled': ['track-atmosphere', 'crowd'],
  // D-EZ-89 — a first-duel feud roll that had nobody left to settle.
  'grant.first-duel-declined': ['confrontation'],

  // ── House rules (D-EZ-83, D-EZ-85) ───────────────────────────────────────
  'temper.calm': ['track-atmosphere', 'pack-racing'],
  'temper.no-driver': ['track-atmosphere'],
  'temper.chip-saved': ['spin', 'pass'],
  'temper.spin': ['spin', 'caution'],
  'temper.loose-car': ['wreck', 'caution'],
  'pace.lapped': ['pass', 'pack-racing'],

  // ── Repairs ──────────────────────────────────────────────────────────────
  'repair.repaired': ['crew', 'paddock'],
  'repair.impossible': ['mechanical', 'paddock'],

  // ── Chips and laps led ───────────────────────────────────────────────────
  'chip.laps-led': ['leader-alone', 'start-finish'],
  // EZRA-S2 §4.0 trait chips. A chip handed over is a pit-box/crew beat, not an
  // on-track one — nothing has happened on the racing surface yet.
  'chip.trait-award': ['crew', 'driver-portrait', 'paddock'],
  'chip.trait-held': ['crew', 'paddock'],
  'chip.trait-restored': ['crew', 'driver-portrait'],
  // D-EZ-28 — a chip coming up from reserve. Bookkeeping at the pit box, like
  // the other chip moments; nothing has happened on the racing surface.
  'chip.reserve-activated': ['crew', 'paddock'],
  // EZRA-S2 §3.12 — a BG chip left behind on climbing out. Pit-box bookkeeping.
  'chip.bg-lost': ['crew', 'paddock'],

  // ── Race Week and the season ─────────────────────────────────────────────
  'raceweek.setup': ['paddock', 'track-atmosphere'],
  'raceweek.grid': ['start-finish', 'pack-racing'],
  'raceweek.placement.pole': ['leader-alone', 'driver-portrait'],
  'raceweek.dnq': ['paddock', 'driver-portrait'],
  'raceweek.bad-weather': ['track-atmosphere', 'crowd'],
  'raceweek.feud': ['confrontation'],
  'raceweek.fume-list': ['confrontation', 'driver-portrait'],
  'raceweek.round-out': ['paddock'],
  'raceweek.open-spaces': ['paddock'],
  'raceweek.chip-forfeit': ['paddock'],
  'raceweek.bubble.did-not-race': ['paddock', 'driver-portrait'],
  'raceweek.uneventful.count': ['track-atmosphere'],
  'raceweek.tv-list.count': ['crowd', 'track-atmosphere'],
  'raceweek.tv-list.gold': ['driver-portrait'],
  'raceweek.tv-list.carry-over': ['crowd'],
  'raceweek.tv-list.extra-draw': ['crowd'],
  'raceweek.tv-event-z.death-suppressed': ['track-atmosphere'],

  'season.entry': ['paddock', 'driver-portrait'],
  'season.result': ['victory-lane', 'start-finish'],
  'season.playoff.seeded': ['driver-portrait', 'victory-lane'],
  'season.rookie-signed': ['driver-portrait', 'paddock'],
  'season.qualifying-chips-unapplied': ['paddock'],

  'career.rookie': ['driver-portrait', 'paddock'],
  'career.rerated': ['driver-portrait'],
  'career.demoted': ['driver-portrait', 'paddock'],
  'career.trait-awarded': ['driver-portrait'],
  'team.crew-regrade': ['crew', 'paddock'],
});

/**
 * Resolve a momentTag to its scenes.
 *
 * The forty `cam.*` tags are matched on their `cam.GROUP` prefix, because the
 * quality suffix is a die roll and not a picture. Everything else is exact.
 */
export function scenesForMoment(momentTag: string): readonly SceneTag[] {
  const exact = MOMENT_TO_SCENE[momentTag];
  if (exact) return exact;
  if (momentTag.startsWith('cam.')) {
    const group = momentTag.split('.').slice(0, 2).join('.');
    const byGroup = MOMENT_TO_SCENE[group];
    if (byGroup) return byGroup;
  }
  return [];
}

/**
 * Which of the given momentTags have no scene at all.
 *
 * **This is the guard that keeps the two layers honest.** The tests run a real
 * season, collect every tag the engine actually emitted, and assert this comes
 * back empty. A new rule that ships a new momentTag without a scene fails the
 * build instead of silently never showing a picture.
 */
export function unmappedMomentTags(tags: Iterable<string>): string[] {
  const missing = new Set<string>();
  for (const t of tags) if (scenesForMoment(t).length === 0) missing.add(t);
  return [...missing].sort();
}

/** Every scene that at least one momentTag can reach. */
export function reachableScenes(): SceneTag[] {
  const s = new Set<SceneTag>();
  for (const list of Object.values(MOMENT_TO_SCENE)) for (const x of list) s.add(x);
  return [...s].sort();
}

// ---------------------------------------------------------------------------
// How many photos each scene actually needs
// ---------------------------------------------------------------------------

/**
 * Measured demand, from 120 races across six track types
 * (`scripts/art-coverage.ts`). These are **per race**, and they are wildly
 * uneven: `challenge.sequence` alone fires 21.75 times a race while
 * `wreck.big-one` fires 0.28 times.
 *
 * The practical consequence: a library with three `pass` photos will show the
 * same picture seven times in one race. Depth matters far more for the common
 * scenes than breadth across the rare ones.
 */
export const SCENE_TARGETS: Readonly<Record<SceneTag, number>> = Object.freeze({
  // ── The heavy hitters. `pass` is demanded ~38 times per race, so this is the
  //    one place where going deep genuinely changes how the game feels.
  'pass': 30,
  'pack-racing': 24,
  'side-by-side': 18,
  'caution': 12,
  'pit-lane': 10,
  'spin': 10,
  'mechanical': 10,
  'leader-alone': 8,
  'pit-stop': 8,
  // ── Occasional.
  'confrontation': 5,
  'wreck': 6,
  'pit-trouble': 4,
  'cockpit': 5,
  'big-wreck': 3,
  'start-finish': 4,
  'track-atmosphere': 4,
  // ── Race Week and season moments. These read as zero in `npm run art`
  //    because that harness runs RACES only, and these are driven by
  //    `runRaceWeek` and the season loop. They are NOT dead weight — they are
  //    simply measured by a different harness.
  'restart': 5,
  'crew': 6,
  'paddock': 6,
  'driver-portrait': 8,
  'crowd': 4,
  'victory-lane': 3,
});

/** What is still missing, worst first — the shopping list. */
export function shortfall(library: readonly ArtImage[]): { scene: SceneTag; have: number; want: number; short: number }[] {
  const have: Record<string, number> = {};
  for (const img of library) for (const s of img.scenes) have[s] = (have[s] ?? 0) + 1;
  return SCENE_TAGS.map((scene) => {
    const h = have[scene] ?? 0;
    const w = SCENE_TARGETS[scene];
    return { scene, have: h, want: w, short: Math.max(0, w - h) };
  })
    .filter((r) => r.short > 0)
    .sort((a, b) => b.short - a.short);
}

// ---------------------------------------------------------------------------
// Naming
// ---------------------------------------------------------------------------

/**
 * The names a photo arrives with, that carry no meaning.
 *
 * These are CDN pipeline words, not words about the picture. `vresize` tells
 * you a content management system made a 940px copy; it tells you nothing you
 * would ever want to read under a photograph.
 */
const JUNK_WORDS = new Set([
  'vresize', 'resize', 'resized', 'crop', 'cropped', 'thumb', 'thumbs', 'thumbnail',
  'scaled', 'scale', 'small', 'medium', 'large', 'xlarge', 'xxlarge', 'full',
  'master', 'default', 'final', 'copy', 'edited', 'orig', 'original', 'output',
  'jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'image', 'images', 'img', 'photo', 'photos',
]);

const IMAGE_EXT = /\.(jpe?g|png|webp|avif|gif|heic|tiff?|bmp)$/i;

/**
 * Is this token an identifier rather than a word — a hash, an asset id, a
 * pixel dimension?
 *
 *   · a long mixed alphanumeric run, e.g. `4dde1f49f57da510VgnVCM100000d7c1a8c0`
 *   · a long pure-digit run, e.g. a Getty asset id
 *   · `940x529`
 *
 * The length floors are the interesting part. 12 for a mixed run leaves
 * `daytona500` and `turn4wreck` alone. 5 for digits leaves a year (`2021`) and
 * a race distance (`500`) alone, and in THIS game those two turn up in real
 * titles constantly.
 *
 * An opaque word is dropped, and it takes any single-letter neighbour in the
 * same segment with it — `h-4dde1f49…` is a hash wearing a one-letter prefix
 * and keeping the `h` would be worse than keeping nothing. That sweep is
 * conditional on a hash actually having been found, which is what lets the `8`
 * in `8_days_of_thunder` survive.
 */
function isOpaqueWord(w: string): boolean {
  if (/^\d+x\d+$/i.test(w)) return true;
  if (/^\d+$/.test(w)) return w.length >= 5;
  return w.length >= 12 && /\d/.test(w) && /[a-z]/i.test(w);
}

/**
 * A pipeline word — meaningless, but it does NOT condemn its neighbours.
 * `daytona_photo_2021` should come out as "daytona 2021", not empty.
 */
const isJunkWord = (w: string): boolean => JUNK_WORDS.has(w.toLowerCase());

/**
 * Turn a filename into a first draft of a human name.
 *
 * `8_days_of_thunder.vresize.940.529.h-4dde…____.webp` → `8 days of thunder`
 *
 * This is only ever a SUGGESTION — the field it fills is editable, and nothing
 * downstream depends on it being right. That is deliberate: a cleverer parser
 * that occasionally eats a real word would be worse than a plain one, because
 * a person retyping a mangled title is more work than a person accepting a
 * dull one.
 *
 * Dot-segments that are nothing but digits are dropped outright (a floor of 3,
 * lower than the word-level floor of 5) because a bare numeric segment in a
 * filename is a CDN dimension essentially every time — `.940.529.` above.
 *
 * Returns '' when nothing survives, so the caller decides what an unnamed photo
 * looks like rather than inheriting a guess like 'Untitled' from down here.
 */
export function suggestTitle(filename: string | null | undefined): string {
  if (!filename) return '';

  // Anything before the last slash is a directory, not a name.
  const base = String(filename).split(/[\\/]/).pop() ?? '';
  let decoded = base;
  try {
    decoded = decodeURIComponent(base);
  } catch {
    // A stray % is not an error worth failing a rename over.
  }

  const stem = decoded.replace(IMAGE_EXT, '');

  const kept: string[] = [];
  for (const segment of stem.split('.')) {
    if (segment.length === 0) continue;
    if (/^\d+$/.test(segment) && segment.length >= 3) continue;

    const words = segment.split(/[_\-+\s]+/).filter((w) => w.length > 0);
    if (words.length === 0) continue;

    const hadId = words.some(isOpaqueWord);
    let real = words.filter((w) => !isOpaqueWord(w) && !isJunkWord(w));
    if (hadId) real = real.filter((w) => w.length > 1);

    if (real.length === 0) continue;
    kept.push(real.join(' '));
  }

  const title = kept.join(' ').replace(/\s+/g, ' ').trim();
  return title.length > 80 ? `${title.slice(0, 80).trimEnd()}…` : title;
}
